import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/common/Button';
import CurrencySelect from '@/components/inputs/CurrencySelect';
import { Modify } from '@/model/common';
import { LiteMarket } from '@/state/reducers/liteMarketSlice';
import { store } from '@/state/store';
import { useCurrencyConfigOptions } from '@/utils/hooks/useCurrencyConfigOptions';
import { useDisclosure } from '@/utils/hooks/useDisclosure';
import useLanguage from '@/utils/hooks/useLanguage';
import { MdAddCircle } from 'react-icons/md';
import { v4 as uuidv4 } from 'uuid';
import Modal, { ModalClose, ModalHeader, ModalTitle } from '../Modal';

interface MarketFormProps {
    items: Modify<LiteMarket, { id: any }>[];
    setItems: (items: Modify<LiteMarket, { id: any }>[]) => void;
}

const isEditMode = false;

const AddMarketForm = ({ items, setItems }: MarketFormProps) => {
    const { currencyOptions } = useCurrencyConfigOptions();

    const lang = useLanguage();

    const [opened, handlers] = useDisclosure(false);
    const fromDiclosure = useDisclosure(false);
    const toDiclosure = useDisclosure(false);

    const defaultFrom = useMemo(() => {
        return { label: lang.lite.pleaseSelectBaseCurrency, ccy: '', value: 'Base ccy', celer: '' };
    }, [lang]);
    const defaultTo = useMemo(() => {
        return { label: lang.lite.pleaseSelectQuoteCurrency, ccy: '', value: 'Quote ccy', celer: '' };
    }, [lang]);

    const [from, onFromChange] = useState(defaultFrom);
    const [to, onToChange] = useState(defaultTo);

    const fromOptions = useMemo(() => {
        const keys = Object.keys(store.getState().marketPair.bidAsk);
        const availableCurrencies = Array.from(new Set(keys.map((key) => key.split('/')[0])));
        return currencyOptions
            .filter((ccy) => availableCurrencies.find((key) => key === ccy.celer))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [fromDiclosure[0]]);

    const toOptions = useMemo(() => {
        const keys = Object.keys(store.getState().marketPair.bidAsk);
        const availablePairs = keys.filter(
            (key) => key.split('/')[0] === from.celer || key.split('/')[1] === from.celer
        );
        return currencyOptions
            .filter((ccy) => !items.find((item) => item.celer === from.celer && item.toCeler === ccy.celer))
            .filter((ccy) => ccy.celer !== from.celer && availablePairs.find((pair) => pair.includes(ccy.celer)))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [toDiclosure[0], fromOptions, from]);

    const onSubmit = useCallback(() => {
        setItems([
            ...items,
            {
                id: uuidv4(),
                name: from.value,
                celer: from.celer,
                logo: from.ccy,
                toName: to.value,
                toCeler: to.celer,
                toLogo: to.ccy
            }
        ]);
        onFromChange(defaultFrom);
        onToChange(defaultTo);
        handlers.close();
    }, [items, from, to]);

    useEffect(() => {
        const temp = toOptions[0] || defaultTo;
        onToChange(temp);
    }, [from]);

    useEffect(() => {
        if (opened) {
            onFromChange(defaultFrom);
            onToChange(defaultTo);
        }
    }, [opened]);

    return (
        <div className="text-brand-text">
            <div
                className="flex w-full group gap-3 p-4 px-3 justify-center items-center cursor-pointer border border-brand-neutral-700 rounded-md text-brand-neutral-400 hover:text-brand-text"
                onClick={handlers.toggle}>
                <MdAddCircle className="w-6 h-6" />
                <div className="text-xs">
                    <div>{lang.lite.clickToAddMarkets}</div>
                    <div className="text-brand-neutral-500 group-hover:text-brand-neutral-300">
                        {lang.lite.youMayArrange}
                    </div>
                </div>
            </div>
            <Modal opened={opened} handlers={handlers} size="max-w-lg">
                <div className="h-full flex flex-col lg:h-auto gap-4 text-brand-text">
                    <div className="flex flex-col gap-1 p-6 pb-0">
                        <ModalHeader className="!p-0">
                            <ModalTitle>
                                {' '}
                                {isEditMode ? lang.lite.editMarketMonitor : lang.lite.addMarketMonitor}
                            </ModalTitle>
                            <ModalClose handlers={handlers} />
                        </ModalHeader>
                        <span className="flex items-center text-brand-neutral-400 text-sm">
                            {lang.lite.pleaseSelectBaseCurrencyToMonitor}
                        </span>
                    </div>
                    <div className="flex-1 basis-0 lg:flex-auto px-6 h-auto min-h-[300px] flex flex-col gap-3">
                        <div className="space-y-4">
                            <div className="flex flex-col gap-3">
                                <CurrencySelect
                                    selected={from}
                                    onSelectChange={onFromChange}
                                    options={fromOptions}
                                    onClick={fromDiclosure[1].toggle}
                                    disabled={!!from.ccy}
                                />
                                {from.ccy && (
                                    <Fragment>
                                        <span className="text-sm text-brand-neutral-400">
                                            {lang.lite.nowSelectCurrencyAgainstBase}
                                        </span>
                                        <CurrencySelect
                                            selected={to}
                                            onSelectChange={onToChange}
                                            options={toOptions}
                                            onClick={toDiclosure[1].toggle}
                                        />
                                        <div
                                            className="cursor-pointer pr-2 -mt-1 text-sm text-right text-brand-text hover:text-brand-primary-light"
                                            onClick={() => onFromChange(defaultFrom)}>
                                            {lang.lite.resetBaseCurrency}
                                        </div>
                                    </Fragment>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end p-6 pt-0">
                        <div className="flex flex-row justify-between text-brand-text text-sm items-center pt-4">
                            <div className="flex justify-end w-full gap-3">
                                <Button
                                    className="w-fit !bg-brand-neutral-700 hover:!bg-brand-neutral-600"
                                    onClick={handlers.close}>
                                    {lang.common.cancel}
                                </Button>
                                <Button className="w-fit" onClick={onSubmit} disabled={!from.ccy || !to.ccy}>
                                    {isEditMode ? lang.common.update : lang.common.confirm}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AddMarketForm;
